/**
 * AppLayout — the shell that wraps every authenticated route.
 *
 * Brief 51.1 (14 Jul 2026) — TopNav cleanup: the four global-surface
 * event listeners that previously lived ONLY on ``Dashboard.jsx`` (so
 * they silently no-op'd from every other page) now live here so Mate
 * and the Command Palette work anywhere in the app.
 *
 *   * ``open-bonsai-mate`` — hoisted to this layout; renders <BonsaiMate/>
 *   * ``open-caseminder-assistant`` — RETIRED (BM-H4115, 9 Oct 2026). The
 *     Assistant was folded into Mate so staff have one helper; Mate now
 *     answers from the SOP and looks up any matter's stage and notes.
 *   * ``open-command-palette`` — hoisted to this layout; renders <CommandPalette/>
 *   * cmd/ctrl-K keyboard shortcut for the palette — also hoisted here
 *   * <FloatingClockWidget/> (29 Sep 2026) — NOT event-gated, unlike the
 *     three above. Mounted unconditionally so a running/paused clock is
 *     visible and controllable from any page ("i thought stop start
 *     clocks were pop outs, no?" — Vanessa). Manages its own visibility
 *     (renders a small "Start a clock" pill when nothing is open) via its
 *     own poll of GET /time-clocks, independent of /my-time's TimeClockList.
 *   * ``open-help`` — DELETED. Help is retired; Mate is now the sole helper.
 *   * ``open-director-tools`` — RETIRED (BM-H626, 26 Sep 2026). The
 *     director tools are the Tools tab of the Directors Lounge
 *     (``/directors``); an old ``/?dl=1`` link forwards there.
 *   * ``open-call-note`` — stays where it lives in ``App.js`` per the
 *     spec guardrail. No touch here.
 */
import { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import TopNavBar from "./TopNavBar";
import BonsaiMate from "./BonsaiMate";
import CommandPalette from "./CommandPalette";
import FloatingClockWidget from "./FloatingClockWidget";

export default function AppLayout() {
  const [mateOpen, setMateOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);

  useEffect(() => {
    const openMate = () => setMateOpen(true);
    const openCmd = () => setCmdOpen(true);
    window.addEventListener("open-bonsai-mate", openMate);
    window.addEventListener("open-command-palette", openCmd);
    return () => {
      window.removeEventListener("open-bonsai-mate", openMate);
      window.removeEventListener("open-command-palette", openCmd);
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    // 29 Oct 2026: the shell is pinned to the window (fixed inset-0). A
    // smooth scrollIntoView on the matter page (e.g. to the "Steps done
    // before CaseMinder" panel) scrolls every scrollable ancestor, the
    // document included when anything overflows it, so the whole frame,
    // sidebar and all, slid up and left the koi background showing under it.
    // A fixed shell does not move when the document scrolls. The wrappers
    // stay overflow-hidden (not clip): specs and staff rely on
    // scrollIntoView reaching controls past the right edge.
    <div className="fixed inset-0 flex h-screen overflow-hidden bg-navy">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNavBar />
        {/*
          BM-H262: `app-scroll-port` marks this element as THE scrolling
          viewport for every routed page. index.css uses it to stop page
          roots asking for a second full viewport of height inside a port
          that is already the viewport minus the top bar. See the
          "app scroll port" block in index.css for the full reasoning.
        */}
        <main className="app-scroll-port flex-1 overflow-y-auto" data-testid="app-scroll-port">
          <Outlet />
        </main>
        {/* Global overlays — mount ONCE at layout level so they fire
            from every page, not only from Dashboard. */}
        <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
        {mateOpen && (
          <BonsaiMate open={mateOpen} onClose={() => setMateOpen(false)} />
        )}
        <FloatingClockWidget />
      </div>
    </div>
  );
}
